import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Input,
  FormControl,
  FormLabel,
  Heading,
  Text,
  VStack,
  Image,
  Center,
  InputGroup,
  InputRightElement,
  IconButton,
  Alert,
  AlertIcon,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { UserContext } from '../Context/UserContext';
import { api } from '../../utils/api';
import logo from '../../Images/logo_tovi.png';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { loginUser } = useContext(UserContext);

  // Dynamic colors
  const bgMain = useColorModeValue('white', 'gray.800');
  const shadow = useColorModeValue('lg', 'dark-lg');
  const inputBg = useColorModeValue('white', 'gray.700');
  const labelColor = useColorModeValue('gray.700', 'gray.300');
  const headingColor = useColorModeValue('gray.700', 'gray.100');
  const footerColor = useColorModeValue('gray.500', 'gray.400');

  const submitHandler = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Clear any existing token before login to prevent expired token issues
    localStorage.removeItem('userToken_TOVIAPPS');

    try {
      const response = await api.post('api/webuser/login', { email, password });
      
      // If null response, there was an auth error
      if (!response) {
        setError('Sessão expirada. Por favor, tente novamente.');
        setIsLoading(false);
        return;
      }

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        // If response body is not JSON
        console.error('Failed to parse response:', parseError);
        setError('Erro ao processar resposta do servidor.');
        setIsLoading(false);
        return;
      }

      if (response.ok) {
        loginUser({
          token: data.token,
          username: data.username,
          email: data.email,
          permissions: data.permissions || ['Suporte'],
          isAdmin: data.isAdmin || false,
        });
        navigate('/main');
      } else {
        setError(data.message || 'Credenciais inválidas. Por favor, tente novamente.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Erro de conexão. Por favor, verifique sua conexão com a internet.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex minH="100vh" align="center" justify="center" bg="gray.50">
      <Box bg={bgMain} rounded="xl" boxShadow={shadow} p={8} w="100%" maxW="400px" textAlign="center">
        <Center mb={6}>
          <Image src={logo} alt="Handyman Logo" maxH="120px" objectFit="contain" />
        </Center>

        <Text fontSize="xl" fontWeight="bold" color="teal.600" mb={2}>
          TOVI App Administração
        </Text>

        <Heading as="h1" size="lg" mb={8} color={headingColor}>
          Login
        </Heading>

        {error && (
          <Alert status="error" mb={6} borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        )}

        <form onSubmit={submitHandler} onKeyDown={(e) => {
          // Prevent form submission on Enter key if button is disabled
          if (e.key === 'Enter' && isLoading) {
            e.preventDefault();
          }
        }}>
          <VStack spacing={5}>
            <FormControl id="email" isRequired>
              <FormLabel color={labelColor}>Email</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu-email@exemplo.com"
                size="md"
                focusBorderColor="teal.500"
                bg={inputBg}
              />
            </FormControl>

            <FormControl id="password" isRequired>
              <FormLabel color={labelColor}>Senha</FormLabel>
              <InputGroup>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite sua senha"
                  size="md"
                  focusBorderColor="teal.500"
                  bg={inputBg}
                />
                <InputRightElement>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <Button
              colorScheme="teal"
              type="submit"
              width="full"
              size="lg"
              mt={4}
              isLoading={isLoading}
              loadingText="Entrando..."
            >
              Entrar
            </Button>
          </VStack>
        </form>

        <Box mt={4}>
          <Link to="/recuperar-senha">
            <Text
              color="teal.600"
              fontSize="sm"
              _hover={{ textDecoration: 'underline' }}
              cursor="pointer"
            >
              Esqueceu sua senha?
            </Text>
          </Link>
        </Box>

        <Text mt={8} fontSize="sm" color={footerColor}>
          © {new Date().getFullYear()} TOVI App. Todos os direitos reservados.
        </Text>
      </Box>
    </Flex>
  );
};

export default LoginPage;
